import { cn } from '@/shared/lib/cn';
import {
  POSTCODE_VALIDATION_MSG,
  isValidPostcode,
  normalisePostcode,
} from '@/shared/lib/validation';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useEffect, useState } from 'react';
import { useUpdateAddress } from '../../hooks/useUsers';
import type { UpdateAddressBody, UserDetail } from '../../types/user';

interface EditAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  user?: Partial<UserDetail>;
}

interface FormState {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  postcode: string;
}

export function EditAddressDialog({ open, onOpenChange, userId, user }: EditAddressDialogProps) {
  const [form, setForm] = useState<FormState>({
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    postcode: '',
  });
  const updateAddress = useUpdateAddress();

  useEffect(() => {
    if (open) {
      setForm({
        addressLine1: user?.addressLine1 ?? '',
        addressLine2: user?.addressLine2 ?? '',
        addressLine3: user?.addressLine3 ?? '',
        postcode: user?.postcode ?? '',
      });
    }
  }, [open, user]);

  const handleChange = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // The postcode is optional — empty clears it. Anything else must be a real UK
  // postcode. Records saved before SC-6525 can already hold junk, so the error
  // shows as soon as the dialog opens rather than only after the field is
  // touched: it has to be corrected (or cleared) before the address can be saved.
  const showPostcodeError = form.postcode.trim() !== '' && !isValidPostcode(form.postcode);
  const isValid = !showPostcodeError;

  const handleSubmit = async () => {
    if (!isValid) return;
    const postcode = form.postcode.trim();
    const data: UpdateAddressBody = {
      addressLine1: form.addressLine1 || null,
      addressLine2: form.addressLine2 || null,
      addressLine3: form.addressLine3 || null,
      postcode: postcode ? normalisePostcode(postcode) : null,
    };
    await updateAddress.mutateAsync({ userId, data });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit Address</DialogTitle>
          <DialogDescription>Update residential address information.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="addressLine1">Address Line 1</Label>
            <Input
              id="addressLine1"
              value={form.addressLine1}
              onChange={(e) => handleChange('addressLine1', e.target.value)}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="addressLine2">Address Line 2</Label>
            <Input
              id="addressLine2"
              value={form.addressLine2}
              onChange={(e) => handleChange('addressLine2', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="addressLine3">City / Town</Label>
            <Input
              id="addressLine3"
              value={form.addressLine3}
              onChange={(e) => handleChange('addressLine3', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="postcode">Postcode</Label>
            <Input
              id="postcode"
              value={form.postcode}
              onChange={(e) => handleChange('postcode', e.target.value)}
              className={cn(showPostcodeError && 'border-red-500 focus-visible:ring-red-500')}
              aria-invalid={showPostcodeError}
            />
            {showPostcodeError && <p className="text-xs text-red-500">{POSTCODE_VALIDATION_MSG}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateAddress.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateAddress.isPending || !isValid}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {updateAddress.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
